/**
 * Słowniki i reguły domenowe P1: klienci, lokalizacje, sprawy, zakresy, kroki procesu
 * (docs/02-model-domenowy.md).
 */

export const PARTY_KINDS = { osoba: 'Osoba prywatna', firma: 'Firma' } as const;
export type PartyKind = keyof typeof PARTY_KINDS;

export const CASE_SOURCES = {
  telefon: 'Telefon',
  email: 'E-mail',
  polecenie: 'Polecenie',
  www: 'Strona WWW',
  studio: 'Wizyta w studiu',
  teren: 'Zgłoszenie z terenu',
  inne: 'Inne',
} as const;
export type CaseSource = keyof typeof CASE_SOURCES;

export const PRIORITIES = { normalny: 'Normalny', pilny: 'Pilny' } as const;
export type Priority = keyof typeof PRIORITIES;

export const BUILDING_TYPES = {
  dom_jednorodzinny: 'Dom jednorodzinny',
  mieszkanie: 'Mieszkanie',
  budynek_wielorodzinny: 'Budynek wielorodzinny',
  lokal_uslugowy: 'Lokal usługowy / biuro',
  hala: 'Hala / budynek przemysłowy',
  inny: 'Inny',
} as const;
export type BuildingType = keyof typeof BUILDING_TYPES;

export const SITE_ROLES = { wlasciciel: 'Właściciel', uzytkownik: 'Użytkownik', zarzadca: 'Zarządca', inny: 'Inna rola' } as const;
export type SiteRole = keyof typeof SITE_ROLES;

export const TRADE_KINDS = { instalacja: 'Instalacja', usluga: 'Usługa / opracowanie' } as const;
export type TradeKind = keyof typeof TRADE_KINDS;

/** Etap sprzedaży zakresu prac (docs/02, 5.1). */
export const SALES_STAGES = {
  otwarty: 'Otwarty',
  ofertowanie: 'Ofertowanie',
  negocjacje: 'Negocjacje',
  zaakceptowany: 'Zaakceptowany',
  umowa_podpisana: 'Umowa podpisana',
  wstrzymany: 'Wstrzymany',
  przegrany: 'Przegrany',
} as const;
export type SalesStage = keyof typeof SALES_STAGES;

/** Kolejność postępu (bez stanów wstrzymany / przegrany). */
export const SALES_PROGRESS: SalesStage[] = ['otwarty', 'ofertowanie', 'negocjacje', 'zaakceptowany', 'umowa_podpisana'];

export const LOST_REASONS = {
  cena: 'Cena',
  konkurencja: 'Wybrał konkurencję',
  rezygnacja: 'Rezygnacja z inwestycji',
  brak_kontaktu: 'Brak kontaktu',
  dofinansowanie: 'Brak dofinansowania',
  techniczne: 'Warunki techniczne',
  inny: 'Inny powód',
} as const;
export type LostReason = keyof typeof LOST_REASONS;

/**
 * Rodzaj zamknięcia sprawy: odrzucone zapytanie (spam, poza obszarem), duplikat, przegrana (rezygnacja klienta,
 * wybór konkurencji — utracona sprzedaż), anulowana albo zrealizowana (wszystko odebrane).
 */
export const CLOSE_KINDS = { odrzucona: 'Odrzucone zapytanie', duplikat: 'Duplikat', przegrana: 'Przegrana', anulowana: 'Anulowana', zrealizowana: 'Zrealizowana' } as const;
export type CloseKind = keyof typeof CLOSE_KINDS;

export const NOTE_KINDS = { notatka: 'Notatka', telefon: 'Rozmowa telefoniczna', email: 'E-mail', spotkanie: 'Spotkanie' } as const;
export type NoteKind = keyof typeof NOTE_KINDS;


/**
 * Normalizacja numeru telefonu do porównań: same cyfry, bez prefiksu kraju 48 / 0048.
 * Zwraca null, gdy numer ma mniej niż 9 cyfr.
 */
export function normalizePhone(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('0048')) d = d.slice(4);
  else if (d.startsWith('48') && d.length === 11) d = d.slice(2);
  return d.length >= 9 ? d : null;
}

export function formatPhone(raw: string): string {
  const n = normalizePhone(raw);
  if (!n || n.length !== 9) return raw.trim();
  return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
}

/** Nazwa wyświetlana kontrahenta. */
export function partyDisplayName(p: { kind: PartyKind; firstName?: string | null; lastName?: string | null; name?: string | null }): string {
  if (p.kind === 'firma') return (p.name ?? '').trim();
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
}

export function siteAddress(s: { street?: string | null; postalCode?: string | null; city: string }): string {
  const tail = [s.postalCode, s.city].filter(Boolean).join(' ');
  return [s.street, tail].filter(Boolean).join(', ');
}

/** Bez e-maila klienta oferta idzie tylko jako PDF (podpowiedź w zadaniu oferty — QA P17). */
export const NO_EMAIL_OFFER_HINT = 'Brak e-maila klienta — ofertę wyślesz tylko jako PDF (pobierz i przekaż klientowi), chyba że uzupełnisz e-mail.';

/**
 * Braki danych sprawy widoczne przy nadawaniu biegu (QA P17) — ostrzeżenie, nie blokada:
 * brak e-maila klienta (ofertę wyślesz tylko jako PDF), adres lokalizacji bez ulicy albo kodu pocztowego.
 */
export function caseDataGaps(c: { emails: string[]; street?: string | null; postalCode?: string | null }): string[] {
  const out: string[] = [];
  if (!c.emails.some((e) => e.trim())) out.push('brak e-maila klienta — ofertę wyślesz tylko jako PDF');
  if (!c.street?.trim()) out.push('adres lokalizacji bez ulicy i numeru');
  if (!c.postalCode?.trim()) out.push('adres lokalizacji bez kodu pocztowego');
  return out;
}

/** QA P20: krótkie nazwy braków (tytuł zadania „Przejmij nową sprawę”, ostrzeżenie w formularzu zapytania). */
export function caseGapsShort(c: { emails?: string[] | null; street?: string | null; postalCode?: string | null }): string[] {
  const out: string[] = [];
  if (c.street !== undefined && !c.street?.trim()) out.push('ulica i numer');
  if (c.postalCode !== undefined && !c.postalCode?.trim()) out.push('kod pocztowy');
  if (c.emails !== undefined && !(c.emails ?? []).some((e) => e.trim())) out.push('e-mail klienta');
  return out;
}

/** QA P20: gotowa treść „Czego brakuje” w oknie „Do uzupełnienia” (pusta, gdy dane są pełne). */
export function caseGapsNote(c: { emails: string[]; street?: string | null; postalCode?: string | null }): string {
  const gaps = caseGapsShort(c);
  return gaps.length ? `${gaps.join(', ')} — do ustalenia z klientem` : '';
}

// ── kod pocztowy → miejscowość (podpowiedź w formularzach adresu) ──

/** Pełny polski kod pocztowy 00-000. */
export const POSTAL_CODE_RE = /^\d{2}-\d{3}$/;

/** Miejscowości dla kodu: najpierw z OpenStreetMap, potem z adresów już wpisanych w CRM. */
export type PostalPlacesDto = {
  code: string;
  places: { name: string; source: 'osm' | 'crm' }[];
  /** usługa zewnętrzna nie odpowiedziała — podpowiedź tylko z adresów w CRM */
  lookupFailed: boolean;
};
